import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { AnsiUp } from 'ansi_up';
import { linkifyFilePaths } from './linkifyFilePaths';
import { linkifySlashCommands, linkifyPlanChoices } from './linkifySlashCommands';
import { embedMedia } from './embedMedia';
import { useSettingsStore } from '../state/settings.store';

marked.setOptions({ breaks: true, gfm: true });

const ansiUp = new AnsiUp();
ansiUp.use_classes = true;

// ANSI escape sequence detector
const ANSI_RE = /\x1b\[/;

/**
 * If text contains ANSI escape codes, convert them to colored HTML spans
 * inside a <pre> block (skip markdown parsing for that content).
 * Otherwise, render as standard markdown with auto-linked file paths.
 */
export function renderMarkdown(text: string, streaming?: boolean): string {
  if (ANSI_RE.test(text)) {
    // Pure ANSI content — render as colored terminal output
    const html = ansiUp.ansi_to_html(text);
    return DOMPurify.sanitize(`<pre class="ansi-output">${html}</pre>`);
  }
  const html = marked.parse(text, { async: false }) as string;
  const sanitized = DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe', 'video', 'audio', 'source', 'details', 'summary'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'playsinline', 'controls', 'preload', 'loading', 'open'],
  });
  // Wrap every <table> in a scroll container so wide tables don't blow out the
  // message bubble on narrow viewports (#1174). The wrapper holds overflow:auto;
  // the <table> stays at default display:table so column widths compute correctly.
  const wrapped = sanitized.replace(
    /<table(\s[^>]*)?>([\s\S]*?)<\/table>/g,
    '<div class="md-table-scroll"><table$1>$2</table></div>',
  );
  // During streaming, skip expensive DOM-based post-processing (linkify + media embed).
  // These run once when streaming ends and the component re-renders with streaming=false.
  if (streaming) return wrapped;
  // settings-zustand-migration Phase 2: reader prefs sourced from the store
  // (migrated from legacy `reader:filePaths` / `reader:slashCmds` / `reader:embedMedia`
  // localStorage keys on first boot — see settings.store.ts runLegacyMigration).
  const settings = useSettingsStore.getState();
  let result = wrapped;
  if (settings.readerFilePaths) {
    result = linkifyFilePaths(result);
  }
  if (settings.readerSlashCmds) {
    result = linkifySlashCommands(result);
    result = linkifyPlanChoices(result);
  }
  if (settings.readerEmbedMedia) {
    result = embedMedia(result);
  }
  return result;
}

/** Convert ANSI escape codes to HTML (for tool activity lines). */
export function renderAnsi(text: string): string {
  if (!ANSI_RE.test(text)) return text;
  return DOMPurify.sanitize(ansiUp.ansi_to_html(text));
}
